import {
  Dropdown,
  MaskedTextField,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import { Controller, useFormContext } from "react-hook-form";

import { simTypeOptions } from "../../constants";
import {
  type MissingAddressFields,
  useZipCodeLookup
} from "../../hooks/useZipCodeLookup";
import {
  LineAdditionalServiceType,
  type LineAdditionalServicesNotesFormValues
} from "../../interfaces";

interface LineAdditionalServiceNotesFormValues {
  type: LineAdditionalServiceType;
  notes: LineAdditionalServicesNotesFormValues;
}

function shouldDisableAddressField(
  fieldName: "city" | "state" | "street" | "neighborhood",
  missingAddressFields: MissingAddressFields
): boolean {
  if (fieldName === "street") return !missingAddressFields.street;

  if (fieldName === "neighborhood") {
    return !missingAddressFields.neighborhood;
  }

  return true;
}

export function LineAdditionalServiceNotesForm(): JSX.Element {
  const { control, watch } =
    useFormContext<LineAdditionalServiceNotesFormValues>();

  const serviceType = watch("type");

  const {
    hasAddress,
    isLookingUpZipCode,
    lookupZipCode,
    missingAddressFields
  } = useZipCodeLookup();

  const isESim = serviceType === LineAdditionalServiceType.ESIM;

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      <Text styles={{ root: { fontWeight: 600 } }}>Dados para solicitação</Text>

      <Controller
        name="type"
        control={control}
        rules={{
          required: "Selecione o tipo de SIM."
        }}
        render={({ field, fieldState }) => (
          <Dropdown
            label="Tipo de ativação C."
            options={simTypeOptions}
            selectedKey={field.value}
            onChange={(_, option) => field.onChange(option?.key)}
            errorMessage={fieldState.error?.message}
            required
          />
        )}
      />

      {isESim ? (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text styles={{ root: { fontWeight: 600 } }}>
            Informações do eSIM
          </Text>
          <Stack horizontal wrap tokens={{ childrenGap: 10 }}>
            <Stack.Item
              grow
              styles={{ root: { flexBasis: 190, minWidth: 180 } }}
            >
              <Controller
                name="notes.eSimInfo.IMEI"
                control={control}
                render={({ field, fieldState }) => (
                  <MaskedTextField
                    label="IMEI"
                    mask="999999999999999"
                    value={field.value ?? ""}
                    onChange={(_, value) =>
                      field.onChange((value ?? "").replace(/\D/g, ""))
                    }
                    errorMessage={fieldState.error?.message}
                    required
                  />
                )}
              />
            </Stack.Item>

            <Stack.Item
              grow={2}
              styles={{ root: { flexBasis: 300, minWidth: 260 } }}
            >
              <Controller
                name="notes.eSimInfo.EID"
                control={control}
                render={({ field, fieldState }) => (
                  <MaskedTextField
                    label="EID"
                    mask="99999999999999999999999999999999"
                    value={field.value ?? ""}
                    onChange={(_, value) =>
                      field.onChange((value ?? "").replace(/\D/g, ""))
                    }
                    errorMessage={fieldState.error?.message}
                    required
                  />
                )}
              />
            </Stack.Item>
          </Stack>
          <Controller
            name="notes.eSimInfo.deviceModel"
            control={control}
            rules={{
              required: "Informe o modelo do aparelho."
            }}
            render={({ field, fieldState }) => (
              <TextField
                label="Modelo do aparelho"
                value={field.value ?? ""}
                onChange={(_, value) => field.onChange(value ?? "")}
                errorMessage={fieldState.error?.message}
                required
              />
            )}
          />
        </Stack>
      ) : (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text styles={{ root: { fontWeight: 600 } }}>
            Endereço de entrega
          </Text>

          <Stack horizontal wrap tokens={{ childrenGap: 10 }}>
            <Stack.Item
              grow
              styles={{ root: { flexBasis: 130, minWidth: 120 } }}
            >
              <Controller
                name="notes.deliveryAddress.zipCode"
                control={control}
                rules={{
                  required: "Informe o CEP."
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="CEP"
                    placeholder="_____-___"
                    value={field.value ?? ""}
                    onChange={(_, value) => void lookupZipCode(value ?? "")}
                    errorMessage={fieldState.error?.message}
                    disabled={isLookingUpZipCode}
                    maxLength={9}
                    required
                  />
                )}
              />
            </Stack.Item>

            {hasAddress && (
              <>
                <Stack.Item
                  grow={2}
                  styles={{ root: { flexBasis: 220, minWidth: 180 } }}
                >
                  <Controller
                    name="notes.deliveryAddress.city"
                    control={control}
                    rules={{ required: "Informe cidade." }}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Cidade"
                        value={String(field.value ?? "")}
                        errorMessage={fieldState.error?.message}
                        disabled={shouldDisableAddressField(
                          "city",
                          missingAddressFields
                        )}
                        required
                      />
                    )}
                  />
                </Stack.Item>

                <Stack.Item styles={{ root: { flexBasis: 72, minWidth: 72 } }}>
                  <Controller
                    name="notes.deliveryAddress.state"
                    control={control}
                    rules={{ required: "Informe estado." }}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Estado"
                        value={String(field.value ?? "")}
                        errorMessage={fieldState.error?.message}
                        maxLength={2}
                        disabled={shouldDisableAddressField(
                          "state",
                          missingAddressFields
                        )}
                        required
                      />
                    )}
                  />
                </Stack.Item>
              </>
            )}
          </Stack>

          {hasAddress && (
            <Stack tokens={{ childrenGap: 8 }}>
              <Stack horizontal wrap tokens={{ childrenGap: 10 }}>
                <Stack.Item
                  grow
                  styles={{ root: { flexBasis: 280, minWidth: 180 } }}
                >
                  <Controller
                    name="notes.deliveryAddress.street"
                    control={control}
                    rules={{ required: "Informe logradouro." }}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Logradouro"
                        value={String(field.value ?? "")}
                        onChange={(_, value) => field.onChange(value ?? "")}
                        errorMessage={fieldState.error?.message}
                        disabled={shouldDisableAddressField(
                          "street",
                          missingAddressFields
                        )}
                        required
                      />
                    )}
                  />
                </Stack.Item>

                <Stack.Item
                  grow
                  styles={{ root: { flexBasis: 180, minWidth: 180 } }}
                >
                  <Controller
                    name="notes.deliveryAddress.neighborhood"
                    control={control}
                    rules={{ required: "Informe bairro." }}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Bairro"
                        value={String(field.value ?? "")}
                        onChange={(_, value) => field.onChange(value ?? "")}
                        errorMessage={fieldState.error?.message}
                        disabled={shouldDisableAddressField(
                          "neighborhood",
                          missingAddressFields
                        )}
                        required
                      />
                    )}
                  />
                </Stack.Item>
              </Stack>

              <Stack horizontal wrap tokens={{ childrenGap: 10 }}>
                <Stack.Item styles={{ root: { flexBasis: 80, minWidth: 80 } }}>
                  <Controller
                    name="notes.deliveryAddress.number"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Número"
                        value={String(field.value ?? "")}
                        onChange={(_, value) => field.onChange(value ?? "")}
                        errorMessage={fieldState.error?.message}
                        maxLength={5}
                      />
                    )}
                  />
                </Stack.Item>

                <Stack.Item
                  grow
                  styles={{ root: { flexBasis: 460, minWidth: 180 } }}
                >
                  <Controller
                    name="notes.deliveryAddress.note"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Complemento"
                        value={String(field.value ?? "")}
                        onChange={(_, value) => field.onChange(value ?? "")}
                        errorMessage={fieldState.error?.message}
                      />
                    )}
                  />
                </Stack.Item>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
